import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
  ValidationErrors,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { TimezoneService } from '@sc/data-access-configuration';
import { TranslatorService } from '@sc/data-access-translation';
import { dayjs, randomId } from '@sc/utils';
import { Dayjs } from 'dayjs';
import { ErrorMessagesService } from '../../services';
import { UtilsValidators } from '../../validators';
import { LabelRequiredPipe } from '../../pipes';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '../icon';
import { ErrorComponent } from '../error';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgIf, NgClass, NgFor, AsyncPipe } from '@angular/common';

@Component({
  selector: 'sc-input-hour',
  styleUrls: ['./input-hour.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <mat-label *ngIf="label" class="sc-design-text-4">
      <label
        [innerHTML]="
          label
            | labelRequired
              : hasRequiredValidator(ngControl?.control)
              : forceHighlightRequired
        "
        [attr.for]="labelId"
      ></label>
    </mat-label>
    <mat-form-field
      appearance="outline"
      [ngClass]="{
        error: isError()
      }"
      (click)="updateSuggestionScroll()"
    >
      <div
        *ngIf="filteredSuggestions && !!value"
        class="sr_only"
        aria-live="polite"
        aria-atomic="true"
      >
        <p [id]="helpTextId">
          {{
            filteredSuggestions.length > 0
              ? ('GlobalApplicativeConstants.accessibility.suggestionsHelpText'
                | translate: { numberSuggestions: filteredSuggestions.length })
              : ('GlobalApplicativeConstants.accessibility.noSuggestionsHelpText'
                | translate: { input: value })
          }}
        </p>
      </div>
      <input
        #mainInput
        matInput
        [id]="labelId"
        type="text"
        [value]="hour"
        [matAutocomplete]="auto"
        [matAutocompleteDisabled]="!filteredSuggestions"
        [disabled]="disabled"
        [aria-describedby]="getAriaDescribedby()"
        [attr.title]="getInputTitle() | async"
        [attr.aria-disabled]="disabled ? true : undefined"
        [attr.aria-required]="
          hasRequiredValidator(ngControl?.control) ? true : undefined
        "
        [attr.aria-invalid]="isError() ? true : undefined"
        (input)="onInputValueChange($event)"
        (blur)="onTouched()"
        (focusin)="onFocusin()"
        (focusout)="onFocusout()"
        class="sc-design-text-3"
      />
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="onInputValueChange($event)"
      >
        <mat-option
          *ngFor="let option of filteredSuggestions"
          [value]="option"
          [id]="'mat-option-' + option"
        >
          {{ option }}
        </mat-option>
      </mat-autocomplete>
    </mat-form-field>
    <sc-error
      *ngIf="displayError()"
      [id]="errorId"
      [class.sc-conf-error-on-input-focus]="focus"
      [error]="getError()"
      [displayCondition]="displayError()"
    ></sc-error>
    <button
      type="button"
      (click)="onIconClick($event)"
      class="accessibility-icon"
      [ngClass]="{ 'has-label': !!label }"
    >
      <sc-icon name="arrow_bottom" aria-hidden="true"></sc-icon>
    </button>
  `,
  imports: [
    NgIf,
    MatFormFieldModule,
    NgClass,
    MatInputModule,
    MatAutocompleteModule,
    NgFor,
    MatOptionModule,
    ErrorComponent,
    IconComponent,
    AsyncPipe,
    TranslateModule,
    LabelRequiredPipe,
  ],
})
export class InputHourComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit
{
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTriggerElement: MatAutocompleteTrigger;

  @ViewChild('mainInput')
  mainInputElement: ElementRef<HTMLInputElement>;

  @ViewChild('auto') autocompleteElement: MatAutocomplete;

  @Input() label: string;

  @Input() timezone: string;
  @Input() disabled: boolean;
  @Input() set _minDate(date: string) {
    this.minDate = this.getMomentValue(date, false);
    setTimeout(() => {
      this.updateFilteredSuggestions();
    }, 100);
  }
  minDate: Dayjs;

  @Input() forceHighlightRequired: boolean;
  @Input() ariaDescribedby = '';

  suggestions: string[] = [];
  filteredSuggestions: string[];

  errorId: string;
  hintId: string;
  labelId: string;
  helpTextId: string;
  autocompleteId: string;

  onChange: any = () => {};
  onTouched: any = () => {};
  onKeyPress: any = () => true;

  changedByInputHour: boolean;
  hourPattern = new RegExp('^([0-1][0-9]|2[0-3]):[0-5][0-9]$');
  hourPatternValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    return !this.hour || this.hourPattern.test(this.hour)
      ? null
      : { pattern: true };
  };
  hourInPeriodValidator = (
    control: AbstractControl
  ): ValidationErrors | null => {
    return !this.minDate ||
      !this.hour ||
      this.minDate.isBefore(this.value, 'day') ||
      (this.minDate.isSame(this.value, 'day') && this.hourSupMin(this.hour))
      ? null
      : { error_without_message: true };
  };

  value: string;
  hour: string;
  focus: boolean;

  constructor(
    @Self()
    @Optional()
    public ngControl: NgControl,
    public changeDetectorRef: ChangeDetectorRef,
    private readonly errorMessagesService: ErrorMessagesService,
    private readonly translator: TranslatorService,
    private readonly timezoneService: TimezoneService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.errorId = randomId(8);
    this.hintId = randomId(8);
    this.labelId = 'input-hour-' + randomId(8);
    this.helpTextId = randomId(8);
    this.autocompleteId = randomId(8);
    for (let hour = 0; hour < 24; hour++) {
      let hourString = hour.toString();
      hourString = hourString.length === 1 ? `0${hourString}` : hourString;
      for (const minutes of ['00', '15', '30', '45']) {
        this.suggestions.push(`${hourString}:${minutes}`);
      }
    }
    this.filteredSuggestions = [...this.suggestions];
  }
  writeValue(value: string): void {
    this.value = value;
    this.updateFilteredSuggestions();

    // Sometimes, after a click on a suggestion, the native value is set to [object Object] even if this.value is a normal string
    const nativeInput = this.mainInputElement
      ?.nativeElement as HTMLInputElement;
    if (nativeInput && nativeInput?.value !== this.hour) {
      nativeInput.value = this.hour;
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngAfterViewInit(): void {
    this.mainInputElement.nativeElement.removeAttribute('aria-haspopup');
  }

  ngOnInit() {
    this.ngControl.control.addValidators([
      this.hourPatternValidator,
      this.hourInPeriodValidator,
    ]);

    this.ngControl.control.valueChanges.subscribe((value) => {
      const previousHour = this.hour;
      if (!this.changedByInputHour) this.setHour(value, this.timezone);
      this.changedByInputHour = false;
      if (this.hour !== previousHour) {
        this.ngControl.control.updateValueAndValidity();
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.timezone?.previousValue !== changes.timezone?.currentValue) {
      this.setHour(this.value, changes.timezone.currentValue);
    }
  }

  onInputValueChange(event: any) {
    if (this.disabled) return;
    let { value: hour } = event?.option || event.target;

    // format hour if recognizable
    hour = hour.replace(/h/g, ':');
    let hourList: string[] = hour.split(':');
    switch (hourList.length) {
      case 1:
        switch (hourList[0].length) {
          case 1:
            // add a 0 before because the hour cannot begin by [3-9] (ex: '6' => '06')
            if (+hour > 2) hour = `0${hour}`;
            break;
          case 2:
            // add a 0 before because the hour cannot be > 23 (ex: '25' => '02:5')
            if (+hour > 23) hour = `0${hour[0]}:${hour[1]}`;
          case 3:
            // add a 0 before because the hour cannot be > 23 (ex: '253' => '02:53')
            if (+hour.substring(0, 2) > 23)
              hour = `0${hour[0]}:${hour.substring(1)}`;
            break;
          case 4:
            // add : to match the format (ex: '1326' => '13:26')
            hour = `${hour.substring(0, 2)}:${hour.substring(2)}`;
            break;
          default:
            break;
        }
        break;
      case 2:
        if (hourList[0].length === 1 && hour[0] > 2) {
          // add the tens before the units of hours (ex: '3:1' => '03:1')
          hour = `0${hour}`;
        }
        break;
    }

    hourList = hour.split(':');
    if (
      hourList.length === 2 &&
      hourList[1].length == 1 &&
      +hourList[1][0] > 5
    ) {
      // add the tens before the units of minutes (ex: '02:6' => '02:06')
      hour = `${hourList[0]}:0${hourList[1]}`;
    }

    this.hour = hour;
    this.changedByInputHour = true;
    const value = this.getMomentValue(this.value, true)
      .tz(this.timezone || this.timezoneService.getDefaultTimezone())
      .hour(+hour.substring(0, 2))
      .minute(+hour.substring(3))
      .second(0);
    if (!this.hourPatternValidator(this.ngControl.control) && value.isValid()) {
      this.writeValue(value.format());
      this.onChange(this.value);
    } else if (!this.value || this.value === '-') {
      this.writeValue('-');
      this.onChange(this.value);
    } else if (this.hourPatternValidator(this.ngControl.control)) {
      this.ngControl.control.updateValueAndValidity();
    }
    this.updateSuggestionScroll();
  }

  onFocusin() {
    this.focus = true;
  }

  onFocusout() {
    this.focus = false;
    this.onTouched();
  }

  onIconClick(event) {
    if (!this.disabled) {
      if (this.filteredSuggestions) {
        event.stopPropagation();
        if (this.autocompleteTriggerElement.panelOpen)
          this.autocompleteTriggerElement.closePanel();
        else this.autocompleteTriggerElement.openPanel();
        this.updateSuggestionScroll();
      }
    }
  }

  updateSuggestionScroll() {
    const hourToScroll =
      this.filteredSuggestions.find((option) => option >= this.hour) ||
      this.filteredSuggestions[this.filteredSuggestions.length - 1];

    const el = document.getElementById(`mat-option-${hourToScroll}`);

    el?.scrollIntoView();
  }

  updateFilteredSuggestions() {
    this.filteredSuggestions =
      !this.minDate || this.minDate.isBefore(this.value, 'day')
        ? this.suggestions
        : this.suggestions.filter((option) => this.hourSupMin(option));
  }

  hourSupMin(hour: string) {
    return (
      hour >
      this.minDate
        .tz(this.timezone || this.timezoneService.getDefaultTimezone())
        .format('HH:mm')
    );
  }

  setHour(value: string, timezone: string) {
    const momentTime = value
      ? dayjs(value).tz(timezone || this.timezoneService.getDefaultTimezone())
      : undefined;
    this.hour = momentTime?.isValid()
      ? momentTime.format('HH:mm')
      : this.hour || '';
  }

  getMomentValue(value: string, useMinDate: boolean) {
    if (value && value !== '-') return dayjs(value);
    if (useMinDate) return this.minDate ? dayjs(this.minDate) : dayjs();
    return;
  }

  getInputTitle() {
    return this.translator.getTranslation(
      'GlobalApplicativeConstants.accessibility.inputTitle',
      {
        label: this.label,
        isRequired:
          this.hasRequiredValidator(this.ngControl?.control) &&
          !this.ngControl?.disabled &&
          !this.disabled,
        value: this.value || '',
        hasValue: !!this.value,
      }
    );
  }

  getAriaDescribedby() {
    const describerIdList = [];

    if (this.isError()) {
      describerIdList.push(this.errorId);
    }

    if (this.filteredSuggestions && !!this.value) {
      describerIdList.push(this.helpTextId);
      describerIdList.push(this.autocompleteId);
    }

    describerIdList.push(this.ariaDescribedby);

    const resultStr = describerIdList.join(' ');

    return resultStr.trim() || undefined;
  }

  getError() {
    if (!this.ngControl?.control) {
      return null;
    }

    return this.errorMessagesService.getError(
      this.ngControl?.control,
      this.label,
      'hourPattern'
    );
  }

  displayError() {
    return !!this.getError() && this.isError();
  }

  isError() {
    return (
      this.ngControl?.control?.invalid &&
      (this.ngControl?.dirty || this.ngControl?.touched)
    );
  }

  hasRequiredValidator(control: AbstractControl): boolean {
    return UtilsValidators.hasRequiredValidator(control);
  }
}
